/**
 * Skills Reference Page
 *
 * Read-only view of all skill configurations for a group.
 * - Coaches see their assigned group only (no dropdown)
 * - Admins get a group selector dropdown
 * - Skills organized by section → category with collapsible sections
 *
 * Requirements: 1.1–8.4
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { restClient } from '../api/rest-client';
import type { Group } from '../api/rest-client';
import { useSkillsStore } from '../store/skillsStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import {
  organizeSkillsBySection,
  deriveCoachGroup,
} from '../utils/skillsReferenceUtils';
import TrainingPlanPanel from '../components/features/training-plans/TrainingPlanPanel';
import { TrainingPlanManager } from '../components/features/training-plans/TrainingPlanManager';

type TrainingTab = 'plan' | 'skills';

export default function SkillsReference() {
  const { isAdmin } = useAuth();

  // Which tab is showing — the page splits into "Training Plan" and "Skills"
  const [activeTab, setActiveTab] = useState<TrainingTab>('plan');

  // Group resolution state
  const [groupName, setGroupName] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupLoading, setGroupLoading] = useState(true);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [noGroupAssignment, setNoGroupAssignment] = useState(false);

  // Skills from store
  const skillsConfig = useSkillsStore((s) => s.skillsConfig);
  const configLoading = useSkillsStore((s) => s.configLoading);
  const configError = useSkillsStore((s) => s.configError);
  const fetchSkillsConfig = useSkillsStore((s) => s.fetchSkillsConfig);

  // Resolve group on mount
  useEffect(() => {
    resolveGroup();
  }, []);

  // Fetch skills when groupName changes
  useEffect(() => {
    if (groupName) {
      fetchSkillsConfig(groupName);
    }
  }, [groupName, fetchSkillsConfig]);

  async function resolveGroup() {
    setGroupLoading(true);
    setGroupError(null);
    setNoGroupAssignment(false);

    try {
      if (isAdmin) {
        const fetchedGroups = await restClient.getGroups();
        const sorted = [...fetchedGroups].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        setGroups(sorted);
        // Admin: leave groupName null so "Choose a group" shows
      } else {
        // Coach path: derive group from dashboard data
        const dashboard = await restClient.getDashboard();
        const riders = dashboard?.my_riders?.riders ?? [];
        const derived = deriveCoachGroup(riders);
        if (derived) {
          setGroupName(derived);
        } else {
          setNoGroupAssignment(true);
        }
      }
    } catch (err) {
      console.error('[SkillsReference] Failed to resolve group:', err);
      setGroupError('Failed to load group information. Please try again.');
    } finally {
      setGroupLoading(false);
    }
  }

  // Organize skills for current group
  const skills = groupName ? skillsConfig[groupName] ?? [] : [];
  const sections = useMemo(() => organizeSkillsBySection(skills), [skills]);
  const totalSkillCount = skills.length;

  // Handle admin group change
  function handleGroupChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setGroupName(e.target.value);
  }

  // Retry handler
  function handleRetry() {
    resolveGroup();
  }

  // --- Loading state ---
  if (groupLoading) {
    return (
      <div className="flex justify-center items-center py-12" aria-live="polite">
        <LoadingSpinner size="lg" />
        <span className="sr-only">Loading training page...</span>
      </div>
    );
  }

  // --- Group resolution error ---
  if (groupError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{groupError}</p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // --- Coach with no group ---
  if (noGroupAssignment) {
    return (
      <div className="text-center py-12">
        <p className="text-primary-600">
          No group assignment found. Please contact an administrator.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <header className="bg-white shadow mb-6">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-primary-900">Training</h1>
        </div>
      </header>

      <div className="container mx-auto px-4">
        {/* Tabs — view the Training Plan and Skills separately */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex gap-6" role="tablist" aria-label="Training sections">
            <button
              type="button"
              role="tab"
              id="tab-plan"
              aria-selected={activeTab === 'plan'}
              aria-controls="panel-plan"
              onClick={() => setActiveTab('plan')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'plan'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              Training Plan
            </button>
            <button
              type="button"
              role="tab"
              id="tab-skills"
              aria-selected={activeTab === 'skills'}
              aria-controls="panel-skills"
              onClick={() => setActiveTab('skills')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'skills'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              Skills
            </button>
          </nav>
        </div>

        {/* Training Plan tab — current week's plan (coaches locked to their group's
            level; admins can switch) plus the admin-only plan editor. */}
        <div
          id="panel-plan"
          role="tabpanel"
          aria-labelledby="tab-plan"
          hidden={activeTab !== 'plan'}
        >
          <div className="mb-6">
            <TrainingPlanPanel coachGroup={groupName} isAdmin={isAdmin} />
          </div>

          {/* Admin-only: create/edit/delete weekly training plans. */}
          {isAdmin && (
            <div className="mb-6">
              <TrainingPlanManager />
            </div>
          )}
        </div>

        {/* Skills tab — its own card with the group selector inside it. */}
        <section
          id="panel-skills"
          role="tabpanel"
          aria-labelledby="tab-skills"
          hidden={activeTab !== 'skills'}
          className="mb-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-2xl font-semibold text-primary-900">Skills</h2>
            {groupName && !configLoading && !configError && (
              <span className="text-sm text-gray-500" aria-label={`${totalSkillCount} skills`}>
                {totalSkillCount} skill{totalSkillCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Admin group selector (coaches are locked to their assigned group) */}
          {isAdmin && groups.length > 0 && (
            <div className="mb-4">
              <label htmlFor="group-selector" className="block text-sm font-medium text-primary-700 mb-1">
                Group
              </label>
              <select
                id="group-selector"
                value={groupName ?? ''}
                onChange={handleGroupChange}
                className="w-full max-w-xs px-3 py-2 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Select a group to view skills"
              >
                <option value="">Choose a group</option>
                {groups.map((g) => (
                  <option key={g.group_id} value={g.name}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Admin hasn't chosen a group yet */}
          {isAdmin && !groupName && !configLoading && (
            <p className="py-6 text-center text-sm text-gray-500">
              Choose a group to view its skills.
            </p>
          )}

          {/* Skills loading */}
          {configLoading && (
            <div className="flex justify-center items-center py-12" aria-live="polite">
              <LoadingSpinner size="lg" />
              <span className="sr-only">Loading skills...</span>
            </div>
          )}

          {/* Skills error */}
          {configError && !configLoading && (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">Failed to load skills. Please try again.</p>
              <button
                onClick={() => groupName && fetchSkillsConfig(groupName)}
                className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty state */}
          {!configLoading && !configError && groupName && skills.length === 0 && (
            <p className="py-6 text-center text-sm text-primary-600">
              No skills are configured for this group.
            </p>
          )}

          {/* Skills sections */}
          {!configLoading && !configError && sections.length > 0 && (
            <div className="space-y-4">
              {sections.map((section) => (
                <details
                  key={section.section}
                  open
                  className="rounded-lg border border-gray-200"
                  aria-label={`${section.section} section`}
                >
                  <summary className="cursor-pointer px-4 py-3 text-lg font-semibold text-primary-900 hover:bg-primary-50 rounded-lg select-none">
                    <h3 className="inline">{section.section}</h3>
                  </summary>
                  <div className="px-4 pb-4 space-y-4">
                    {section.categories.map((cat) => (
                      <div key={cat.category}>
                        <h4 className="text-base font-medium text-primary-800 mb-2 border-b border-primary-100 pb-1">
                          {cat.category}
                        </h4>
                        <ul className="space-y-2">
                          {cat.skills.map((skill) => (
                            <li key={skill.skill_id} className="pl-4">
                              <p className="font-medium text-primary-900">{skill.skill_name}</p>
                              {skill.description && (
                                <p className="text-sm text-primary-600">{skill.description}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
